import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Format currency for display
const formatCurrency = (amount, currency = 'JMD') => {
  return new Intl.NumberFormat('en-JM', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount);
};

// Format date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-JM', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Generate a PDF invoice for a payment
 * @param {Object} invoiceData - Invoice details
 * @param {string} invoiceData.invoiceNumber - Invoice number (e.g., INV-2024-00001)
 * @param {Date} invoiceData.issuedDate - Date invoice was issued
 * @param {Date} invoiceData.dueDate - Payment due date (or null if paid)
 * @param {Date} invoiceData.paidDate - Date payment was received (or null if unpaid)
 * @param {Object} invoiceData.vendor - Vendor/business details
 * @param {string} invoiceData.vendor.name - Business name
 * @param {string} invoiceData.vendor.email - Business email
 * @param {string} invoiceData.vendor.phone - Business phone
 * @param {string} invoiceData.vendor.address - Business address
 * @param {Object} invoiceData.subscription - Subscription details
 * @param {string} invoiceData.subscription.tier - Tier name (basic/premium)
 * @param {string} invoiceData.subscription.billingPeriod - monthly/annual
 * @param {Date} invoiceData.subscription.periodStart - Start of billing period
 * @param {Date} invoiceData.subscription.periodEnd - End of billing period
 * @param {number} invoiceData.amount - Invoice amount
 * @param {string} invoiceData.currency - Currency code (default: JMD)
 * @param {string} invoiceData.paymentMethod - How payment was made
 * @param {string} invoiceData.transactionRef - Transaction reference
 * @param {Object} invoiceData.companyDetails - Your company details
 * @returns {jsPDF} - The PDF document
 */
export const generateInvoicePDF = (invoiceData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Colors
  const primaryColor = [5, 150, 105]; // Emerald-600
  const grayColor = [107, 114, 128];
  const darkColor = [17, 24, 39];

  // Company details (can be customized)
  const companyDetails = invoiceData.companyDetails || {
    name: 'Jamaica Motorist Guide',
    tagline: 'Your Trusted Resource for Motoring in Jamaica',
    email: 'info@jamaicadriversguide.com',
    website: 'www.jamaicadriversguide.com',
    address: 'Kingston, Jamaica'
  };

  // Header background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 45, 'F');

  // Company name
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(companyDetails.name, 20, 20);

  // Company tagline
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(companyDetails.tagline, 20, 28);

  // Invoice label
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', pageWidth - 20, 22, { align: 'right' });

  // Status badge
  const isPaid = !!invoiceData.paidDate;
  doc.setFontSize(10);
  doc.setFillColor(isPaid ? 16 : 245, isPaid ? 185 : 158, isPaid ? 129 : 11);
  doc.roundedRect(pageWidth - 50, 28, 30, 10, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text(isPaid ? 'PAID' : 'UNPAID', pageWidth - 35, 35, { align: 'center' });

  // Reset text color
  doc.setTextColor(...darkColor);

  // Invoice details section
  let yPos = 60;

  // Left column - Bill To
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayColor);
  doc.text('BILL TO', 20, yPos);

  doc.setTextColor(...darkColor);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(invoiceData.vendor.name, 20, yPos + 8);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  if (invoiceData.vendor.email) {
    doc.text(invoiceData.vendor.email, 20, yPos + 16);
  }
  if (invoiceData.vendor.phone) {
    doc.text(invoiceData.vendor.phone, 20, yPos + 24);
  }
  if (invoiceData.vendor.address) {
    doc.text(invoiceData.vendor.address, 20, yPos + 32);
  }

  // Right column - Invoice details
  const rightCol = pageWidth - 80;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayColor);
  doc.text('INVOICE DETAILS', rightCol, yPos);

  const detailsData = [
    ['Invoice #:', invoiceData.invoiceNumber],
    ['Issue Date:', formatDate(invoiceData.issuedDate)],
    ['Due Date:', invoiceData.dueDate ? formatDate(invoiceData.dueDate) : 'N/A'],
  ];

  if (isPaid) {
    detailsData.push(['Paid Date:', formatDate(invoiceData.paidDate)]);
  }

  doc.setTextColor(...darkColor);
  doc.setFontSize(10);
  detailsData.forEach((row, i) => {
    doc.setFont('helvetica', 'normal');
    doc.text(row[0], rightCol, yPos + 8 + (i * 8));
    doc.setFont('helvetica', 'bold');
    doc.text(row[1], rightCol + 40, yPos + 8 + (i * 8));
  });

  // Line items table
  yPos = 115;

  const tierName = invoiceData.subscription.tier.charAt(0).toUpperCase() +
    invoiceData.subscription.tier.slice(1);
  const periodLabel = invoiceData.subscription.billingPeriod === 'annual' ? 'Annual' : 'Monthly';

  const tableData = [
    [
      `${tierName} Tier Subscription (${periodLabel})`,
      `${formatDate(invoiceData.subscription.periodStart)} - ${formatDate(invoiceData.subscription.periodEnd)}`,
      formatCurrency(invoiceData.amount, invoiceData.currency || 'JMD')
    ]
  ];

  doc.autoTable({
    startY: yPos,
    head: [['Description', 'Period', 'Amount']],
    body: tableData,
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 10
    },
    bodyStyles: {
      fontSize: 10,
      cellPadding: 8
    },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 60 },
      2: { cellWidth: 40, halign: 'right' }
    },
    margin: { left: 20, right: 20 },
    theme: 'striped'
  });

  // Totals section
  const finalY = doc.lastAutoTable.finalY + 10;

  // Totals box
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(pageWidth - 90, finalY, 70, 40, 3, 3, 'F');

  doc.setFontSize(10);
  doc.setTextColor(...grayColor);
  doc.text('Subtotal:', pageWidth - 85, finalY + 12);
  doc.text('Tax (0%):', pageWidth - 85, finalY + 22);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Total:', pageWidth - 85, finalY + 34);

  doc.setTextColor(...darkColor);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(invoiceData.amount), pageWidth - 25, finalY + 12, { align: 'right' });
  doc.text(formatCurrency(0), pageWidth - 25, finalY + 22, { align: 'right' });

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(invoiceData.amount), pageWidth - 25, finalY + 34, { align: 'right' });

  // Payment details (if paid)
  if (isPaid) {
    const paymentY = finalY + 55;
    doc.setFillColor(209, 250, 229);
    doc.roundedRect(20, paymentY, pageWidth - 40, 25, 3, 3, 'F');

    doc.setTextColor(6, 95, 70);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Received', 30, paymentY + 10);

    doc.setFont('helvetica', 'normal');
    const paymentInfo = [
      `Method: ${invoiceData.paymentMethod || 'N/A'}`,
      invoiceData.transactionRef ? `Reference: ${invoiceData.transactionRef}` : ''
    ].filter(Boolean).join('  |  ');
    doc.text(paymentInfo, 30, paymentY + 18);
  }

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 30;

  doc.setDrawColor(229, 231, 235);
  doc.line(20, footerY - 10, pageWidth - 20, footerY - 10);

  doc.setTextColor(...grayColor);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Thank you for your business!', pageWidth / 2, footerY, { align: 'center' });
  doc.text(
    `${companyDetails.email}  |  ${companyDetails.website}`,
    pageWidth / 2,
    footerY + 8,
    { align: 'center' }
  );
  doc.text(companyDetails.address, pageWidth / 2, footerY + 16, { align: 'center' });

  return doc;
};

/**
 * Generate and download an invoice PDF
 * @param {Object} invoiceData - Invoice details (see generateInvoicePDF)
 */
export const downloadInvoice = (invoiceData) => {
  const doc = generateInvoicePDF(invoiceData);
  doc.save(`Invoice-${invoiceData.invoiceNumber}.pdf`);
};

/**
 * Generate and open invoice in new tab
 * @param {Object} invoiceData - Invoice details (see generateInvoicePDF)
 */
export const previewInvoice = (invoiceData) => {
  const doc = generateInvoicePDF(invoiceData);
  const blobUrl = doc.output('bloburl');
  window.open(blobUrl, '_blank');
};

/**
 * Generate invoice data from payment and vendor records
 * @param {Object} payment - Payment record from database
 * @param {Object} vendor - Vendor/service provider record
 * @param {Object} subscription - Subscription record
 * @returns {Object} Invoice data ready for PDF generation
 */
export const createInvoiceDataFromPayment = (payment, vendor, subscription) => {
  // Generate invoice number if not provided
  const invoiceNumber = payment.invoice_number ||
    `INV-${new Date().getFullYear()}-${String(payment.id).slice(-5).padStart(5, '0')}`;

  return {
    invoiceNumber,
    issuedDate: payment.payment_date || new Date(),
    paidDate: payment.status === 'completed' ? payment.payment_date : null,
    dueDate: null, // Already paid
    vendor: {
      name: vendor.name,
      email: vendor.email,
      phone: vendor.phone,
      address: vendor.address || vendor.parish || ''
    },
    subscription: {
      tier: subscription?.tier || vendor.tier || 'basic',
      billingPeriod: subscription?.billing_period || 'monthly',
      periodStart: payment.period_covered_start || subscription?.current_period_start,
      periodEnd: payment.period_covered_end || subscription?.current_period_end
    },
    amount: payment.amount,
    currency: payment.currency || 'JMD',
    paymentMethod: payment.payment_method,
    transactionRef: payment.transaction_ref
  };
};

export default {
  generateInvoicePDF,
  downloadInvoice,
  previewInvoice,
  createInvoiceDataFromPayment
};
